import sectionSourceAnalysis from "@/Templates/Doccure/.generated/section-source-analysis.json";

type Primitive = string | number | boolean | null | undefined;

type SectionAnalysisRepeatedGroup = {
  key: string;
  mockItems?: Array<Record<string, Primitive>>;
};

type SectionAnalysisEntry = {
  sectionKey: string;
  relativePath: string;
  singletonDefaults?: Record<string, Primitive>;
  repeatedJsxGroups?: SectionAnalysisRepeatedGroup[];
};

type SectionAnalysisPayload = {
  sections: SectionAnalysisEntry[];
};

function toPascalCase(value: string) {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_/.]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/(^\w|\s+\w)/g, (match) =>
      match.replace(/\s+/g, "").toUpperCase(),
    );
}

function toCamelCase(value: string) {
  const pascal = toPascalCase(value);
  return pascal ? `${pascal.charAt(0).toLowerCase()}${pascal.slice(1)}` : value;
}

const analysisPayload =
  sectionSourceAnalysis as unknown as SectionAnalysisPayload;
const sectionEntryMap = new Map(
  analysisPayload.sections.map(
    (section) =>
      [section.sectionKey, section] satisfies [string, SectionAnalysisEntry],
  ),
);

export function getSectionDefaultProps<T extends Record<string, unknown>>(
  sectionKey: string,
  overrides?: Partial<T>,
): T {
  const sectionEntry = sectionEntryMap.get(sectionKey);
  if (!sectionEntry) {
    throw new Error(
      `Doccure section analysis not found for key: ${sectionKey}`,
    );
  }

  const repeatedDefaults = Object.fromEntries(
    (sectionEntry.repeatedJsxGroups ?? []).map((group) => [
      toCamelCase(group.key),
      (group.mockItems ?? []).map((item, index) => ({
        key: `${toCamelCase(group.key)}-${index + 1}`,
        ...item,
      })),
    ]),
  );

  return {
    ...(sectionEntry.singletonDefaults ?? {}),
    ...repeatedDefaults,
    ...(overrides ?? {}),
  } as T;
}
